import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Eye, Users, FileText, Briefcase, Send, LogOut, Plus, Trash2, Edit2, X, Check,
  BarChart3, Mail, Globe, ChevronDown, ChevronUp, Film, RefreshCw, MessageSquare, Settings, Inbox, Volume2, UserPlus
} from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import PortfolioAdminPanel from "@/PortfolioAdminPanel";
import TalentAdminPanel from "@/TalentAdminPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Login Screen ──
const LoginScreen = ({ onLogin }) => {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem("admin_token", data.token);
      onLogin(data.token);
    } catch {
      setError("Invalid password");
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0B0D] flex items-center justify-center px-6">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className="glass-card rounded-xl p-8 w-full max-w-sm">
        <div className="text-center mb-6">
          <img src="/graphikpro-logo.png" alt="Graphikpro" className="h-8 w-auto" />
          <p className="mono-font text-xs text-[#52525B] mt-2 uppercase tracking-wider">Admin Panel</p>
        </div>
        <form onSubmit={handleLogin}>
          <label className="mono-font text-xs text-[#52525B] mb-2 block uppercase tracking-wider">Password</label>
          <input data-testid="admin-password" type="password" className="glass-input mb-4" placeholder="Enter admin password"
            value={password} onChange={(e) => { setPassword(e.target.value); setError(""); }} />
          {error && <p className="text-red-400 text-sm mb-3">{error}</p>}
          <button data-testid="admin-login-btn" type="submit" className="glow-button w-full">Log In</button>
        </form>
      </motion.div>
    </div>
  );
};

// ── Stat Card ──
const StatCard = ({ icon: Icon, label, value, color = "#17B352" }) => (
  <div className="glass-card rounded-xl p-5">
    <div className="flex items-center gap-3 mb-3">
      <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${color}15` }}>
        <Icon className="w-4.5 h-4.5" style={{ color }} />
      </div>
      <span className="mono-font text-[10px] text-[#52525B] uppercase tracking-wider">{label}</span>
    </div>
    <div className="text-3xl font-bold" style={{ color }}>{value}</div>
  </div>
);

// ── Job Form Modal ──
const JobFormModal = ({ job, onSave, onClose, token }) => {
  const [form, setForm] = useState(job || {
    title: "", department: "", location: "", type: "full-time", description: "", requirements: "", is_active: true
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
      if (job) {
        await axios.put(`${API}/admin/jobs/${job.id}`, form, { headers });
      } else {
        await axios.post(`${API}/admin/jobs`, form, { headers });
      }
      toast.success(job ? "Job updated" : "Job created");
      onSave();
    } catch {
      toast.error("Failed to save job");
    } finally {
      setSaving(false);
    }
  };

  const set = (k, v) => setForm({ ...form, [k]: v });

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className="glass-card rounded-xl p-6 w-full max-w-lg max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-xl">{job ? "Edit Job" : "New Job Posting"}</h3>
          <button onClick={onClose} className="text-[#52525B] hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="mono-font text-[10px] text-[#52525B] mb-1 block uppercase tracking-wider">Title</label>
            <input data-testid="job-form-title" className="glass-input" required value={form.title} onChange={(e) => set("title", e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mono-font text-[10px] text-[#52525B] mb-1 block uppercase tracking-wider">Department</label>
              <input data-testid="job-form-dept" className="glass-input" required value={form.department} onChange={(e) => set("department", e.target.value)} />
            </div>
            <div>
              <label className="mono-font text-[10px] text-[#52525B] mb-1 block uppercase tracking-wider">Location</label>
              <input data-testid="job-form-location" className="glass-input" required value={form.location} onChange={(e) => set("location", e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mono-font text-[10px] text-[#52525B] mb-1 block uppercase tracking-wider">Type</label>
              <select data-testid="job-form-type" className="glass-input" value={form.type} onChange={(e) => set("type", e.target.value)}>
                <option value="full-time">Full-time</option>
                <option value="part-time">Part-time</option>
                <option value="contract">Contract</option>
                <option value="freelance">Freelance</option>
              </select>
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.is_active} onChange={(e) => set("is_active", e.target.checked)}
                  className="w-4 h-4 accent-[#17B352]" />
                <span className="mono-font text-xs text-[#A1A1AA]">Active</span>
              </label>
            </div>
          </div>
          <div>
            <label className="mono-font text-[10px] text-[#52525B] mb-1 block uppercase tracking-wider">Description</label>
            <textarea data-testid="job-form-desc" className="glass-textarea" required rows={4}
              value={form.description} onChange={(e) => set("description", e.target.value)} />
          </div>
          <div>
            <label className="mono-font text-[10px] text-[#52525B] mb-1 block uppercase tracking-wider">Requirements</label>
            <textarea data-testid="job-form-reqs" className="glass-textarea" required rows={4}
              value={form.requirements} onChange={(e) => set("requirements", e.target.value)} />
          </div>
          <div className="flex gap-3 pt-2">
            <button data-testid="job-form-save" type="submit" disabled={saving}
              className="glow-button flex items-center gap-2 disabled:opacity-50">
              {saving ? "Saving..." : <><Check className="w-4 h-4" /> {job ? "Update" : "Create"}</>}
            </button>
            <button type="button" onClick={onClose} className="secondary-button text-sm py-2 px-5">Cancel</button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

// ── Email status badge (per submission) ──
const STATUS_COLORS = {
  queued: { dot: "#52525B", bg: "rgba(82,82,91,0.12)", label: "Queued" },
  sent: { dot: "#A1A1AA", bg: "rgba(161,161,170,0.12)", label: "Sent" },
  delivery_delayed: { dot: "#FFD60A", bg: "rgba(255,214,10,0.12)", label: "Delayed" },
  delivered: { dot: "#32D74B", bg: "rgba(50,215,75,0.12)", label: "Delivered" },
  opened: { dot: "#32D74B", bg: "rgba(50,215,75,0.18)", label: "Opened" },
  clicked: { dot: "#0AFFB4", bg: "rgba(10,255,180,0.15)", label: "Clicked" },
  bounced: { dot: "#FF453A", bg: "rgba(255,69,58,0.12)", label: "Bounced" },
  complained: { dot: "#FF453A", bg: "rgba(255,69,58,0.18)", label: "Spam" },
};

const EmailStatusBadge = ({ kind, status }) => {
  if (!status) {
    return <span className="mono-font text-[9px] text-[#3f3f46] uppercase">—</span>;
  }
  const c = STATUS_COLORS[status.status] || STATUS_COLORS.queued;
  return (
    <span
      data-testid={`email-status-${kind}-${status.status}`}
      title={`${kind === "auto_reply" ? "Auto-reply" : "Team notification"} · ${c.label}${status.last_event_at ? " · " + new Date(status.last_event_at).toLocaleString() : ""}`}
      className="inline-flex items-center gap-1.5 mono-font text-[9px] tracking-wider uppercase px-2 py-0.5 rounded-full"
      style={{ backgroundColor: c.bg, color: c.dot }}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: c.dot }} />
      {c.label}
    </span>
  );
};

const EmailStatusCell = ({ row }) => {
  const es = row.email_status || {};
  if (!row.notification_email_id && !row.auto_reply_email_id) {
    return <span className="mono-font text-[9px] text-[#3f3f46] uppercase">pre-tracking</span>;
  }
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-1.5">
        <span className="mono-font text-[8px] text-[#52525B] uppercase tracking-wider w-12">Team</span>
        <EmailStatusBadge kind="team" status={es.notification} />
      </div>
      <div className="flex items-center gap-1.5">
        <span className="mono-font text-[8px] text-[#52525B] uppercase tracking-wider w-12">Reply</span>
        <EmailStatusBadge kind="auto_reply" status={es.auto_reply} />
      </div>
    </div>
  );
};

// ── Email Log Panel ──
const EmailLogPanel = ({ token }) => {
  const [logs, setLogs] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [timeline, setTimeline] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter === "all" ? "" : `&kind=${filter}`;
      const { data } = await axios.get(`${API}/admin/email-log?limit=100${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLogs(data || []);
    } catch {
      toast.error("Failed to load email log");
    } finally {
      setLoading(false);
    }
  }, [filter, token]);

  useEffect(() => { load(); }, [load]);

  const loadTimeline = async (eid) => {
    if (timeline[eid]) {
      setExpanded(expanded === eid ? null : eid);
      return;
    }
    try {
      const { data } = await axios.get(`${API}/admin/email-log/${eid}/events`,
        { headers: { Authorization: `Bearer ${token}` } });
      setTimeline({ ...timeline, [eid]: data || [] });
      setExpanded(eid);
    } catch {
      toast.error("Could not load timeline");
    }
  };

  return (
    <div className="space-y-4" data-testid="email-log-panel">
      <div className="glass-card rounded-xl p-5">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <Inbox className="w-5 h-5 text-[#17B352]" />
            <h3 className="text-lg">Outbound Email Log</h3>
            <span className="mono-font text-xs text-[#52525B]">({logs.length})</span>
          </div>
          <div className="flex items-center gap-2">
            {["all", "contact", "translator", "job"].map((k) => (
              <button key={k} data-testid={`email-log-filter-${k}`}
                onClick={() => setFilter(k)}
                className={`mono-font text-[10px] uppercase tracking-wider px-3 py-1.5 rounded-full border transition-all ${
                  filter === k ? "border-[#17B352] text-[#17B352] bg-[rgba(23,179,82,0.08)]" : "border-[#1f1f23] text-[#A1A1AA] hover:text-[#17B352]"
                }`}>{k}</button>
            ))}
            <button onClick={load}
              className="mono-font text-[10px] uppercase tracking-wider px-3 py-1.5 rounded-full border border-[#1f1f23] text-[#A1A1AA] hover:text-[#17B352] inline-flex items-center gap-1.5">
              <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
          </div>
        </div>

        {loading ? (
          <p className="mono-font text-xs text-[#52525B]">Loading...</p>
        ) : logs.length === 0 ? (
          <p className="mono-font text-xs text-[#52525B]">No outbound emails for this filter yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[rgba(255,255,255,0.05)]">
                  {["Status", "Subject", "To", "Sent", "Engagement", ""].map((h) => (
                    <th key={h} className="px-3 py-2 text-left mono-font text-[10px] text-[#52525B] uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {logs.map((r) => (
                  <React.Fragment key={r.email_id}>
                    <tr className="border-b border-[rgba(255,255,255,0.03)] hover:bg-[rgba(255,255,255,0.02)]">
                      <td className="px-3 py-3"><EmailStatusBadge kind="log" status={{ status: r.status, last_event_at: r.last_event_at }} /></td>
                      <td className="px-3 py-3 text-[#E5E5E5] max-w-md"><span className="line-clamp-1 block">{r.subject || "—"}</span></td>
                      <td className="px-3 py-3 text-[#A1A1AA] text-xs"><span className="line-clamp-1 block">{(r.to || []).join(", ")}</span></td>
                      <td className="px-3 py-3 mono-font text-[10px] text-[#52525B] uppercase whitespace-nowrap">
                        {r.sent_at ? new Date(r.sent_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex gap-1.5">
                          {r.opened && <span title="Opened" className="w-2 h-2 rounded-full bg-[#32D74B]" />}
                          {r.clicked && <span title="Clicked" className="w-2 h-2 rounded-full bg-[#0AFFB4]" />}
                          {r.bounced && <span title="Bounced" className="w-2 h-2 rounded-full bg-[#FF453A]" />}
                          {r.complained && <span title="Spam" className="w-2 h-2 rounded-full bg-[#FF453A]" />}
                          {!r.opened && !r.clicked && !r.bounced && !r.complained && <span className="mono-font text-[9px] text-[#3f3f46]">—</span>}
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <button data-testid={`email-log-expand-${r.email_id?.slice(0,8)}`}
                          onClick={() => loadTimeline(r.email_id)}
                          className="mono-font text-[9px] uppercase tracking-wider text-[#17B352] hover:underline">
                          {expanded === r.email_id ? "Hide" : "Timeline"}
                        </button>
                      </td>
                    </tr>
                    {expanded === r.email_id && (
                      <tr className="bg-[rgba(23,179,82,0.02)]">
                        <td colSpan={6} className="px-3 py-3">
                          <div className="space-y-1">
                            {(timeline[r.email_id] || []).length === 0 ? (
                              <p className="mono-font text-[10px] text-[#52525B]">No events recorded yet.</p>
                            ) : (
                              (timeline[r.email_id] || []).map((ev, i) => (
                                <div key={i} className="flex items-center gap-3 mono-font text-[10px] uppercase tracking-wider">
                                  <span className="text-[#52525B] w-32">{ev.created_at ? new Date(ev.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "—"}</span>
                                  <span className="text-[#17B352]">{ev.type?.replace("email.", "")}</span>
                                </div>
                              ))
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Site Audio (admin upload — up to 3 clips, random rotation) ──
const SiteAudioPanel = ({ token }) => {
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/site-audio`);
      setInfo(data);
    } catch {}
  }, []);

  useEffect(() => { load(); }, [load]);

  const tracks = info?.tracks || [];
  const maxTracks = info?.max_tracks ?? 3;
  const volume = info?.volume ?? 0.18;
  const enabled = info?.enabled ?? true;
  const slotsLeft = Math.max(0, maxTracks - tracks.length);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 12 * 1024 * 1024) {
      toast.error("File too large (max 12 MB)");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/admin/site-audio/upload`, fd, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      setInfo(data);
      toast.success(`Added: ${file.name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeTrack = async (filename) => {
    if (!window.confirm(`Remove "${filename}"?`)) return;
    setBusy(true);
    try {
      const { data } = await axios.delete(`${API}/admin/site-audio/track/${encodeURIComponent(filename)}`,
        { headers: { Authorization: `Bearer ${token}` } });
      setInfo(data);
      toast.success("Track removed");
    } catch {
      toast.error("Remove failed");
    } finally { setBusy(false); }
  };

  const setEnabled = async (next) => {
    setBusy(true);
    try {
      const { data } = await axios.put(`${API}/admin/site-audio`, { enabled: next },
        { headers: { Authorization: `Bearer ${token}` } });
      setInfo(data);
      toast.success(next ? "Audio enabled" : "Audio disabled (synth fallback active)");
    } catch { toast.error("Update failed"); }
    finally { setBusy(false); }
  };

  const setVolume = async (v) => {
    try {
      const { data } = await axios.put(`${API}/admin/site-audio`, { volume: v },
        { headers: { Authorization: `Bearer ${token}` } });
      setInfo(data);
    } catch {}
  };

  const removeAll = async () => {
    if (!window.confirm("Remove ALL uploaded tracks and revert to the synth drone?")) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/site-audio`, { headers: { Authorization: `Bearer ${token}` } });
      await load();
      toast.success("All tracks removed — synth drone active");
    } catch { toast.error("Remove failed"); }
    finally { setBusy(false); }
  };

  const hasTracks = tracks.length > 0;

  return (
    <div className="glass-card rounded-xl p-5 space-y-4" data-testid="site-audio-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Volume2 className="w-5 h-5 text-[#17B352]" />
          <h3 className="text-lg">Site background music</h3>
        </div>
        <span
          data-testid="site-audio-state"
          className={`mono-font text-[10px] uppercase tracking-wider px-3 py-1 rounded-full ${
            hasTracks && enabled ? "text-[#32D74B] bg-[rgba(50,215,75,0.1)]" :
            hasTracks && !enabled ? "text-[#A1A1AA] bg-[rgba(82,82,91,0.1)]" :
            "text-[#A1A1AA] bg-[rgba(82,82,91,0.1)]"
          }`}>
          {hasTracks && enabled ? `${tracks.length}/${maxTracks} · Live` : hasTracks ? "Disabled" : "Synth fallback"}
        </span>
      </div>
      <p className="text-[#A1A1AA] text-sm">
        Upload up to {maxTracks} clips (MP3 / M4A / OGG / WAV / AAC, max 12 MB each). They play in random rotation. Filenames are preserved exactly as uploaded.
      </p>

      {/* Track list */}
      <div className="space-y-2" data-testid="site-audio-tracks">
        {tracks.map((t) => (
          <div key={t.filename} className="rounded-lg border border-[#1f1f23] p-3 bg-[#0B0B0D] flex items-center gap-3" data-testid={`audio-track-row-${t.filename}`}>
            <div className="w-9 h-9 rounded-full bg-[rgba(23,179,82,0.08)] border border-[#17B352]/20 flex items-center justify-center shrink-0">
              <Volume2 className="w-4 h-4 text-[#17B352]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-white truncate">{t.original_filename || t.filename}</p>
              <p className="mono-font text-[10px] text-[#52525B] uppercase tracking-wider truncate">
                {t.filename}{t.size ? ` · ${Math.round((t.size||0)/1024)} KB` : ""}
              </p>
            </div>
            <audio controls preload="none" className="hidden sm:block max-w-[200px]"
              src={t.url.startsWith("http") ? t.url : `${process.env.REACT_APP_BACKEND_URL}${t.url}`} />
            <button data-testid={`audio-track-remove-${t.filename}`}
              onClick={() => removeTrack(t.filename)}
              disabled={busy}
              title="Remove track"
              className="text-[#FF453A] hover:bg-[rgba(255,69,58,0.05)] rounded-full p-2">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
        {!hasTracks && (
          <p className="mono-font text-xs text-[#52525B]">No tracks yet — synth ambient drone is active.</p>
        )}
      </div>

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-3">
        {slotsLeft > 0 ? (
          <label className="inline-flex items-center gap-2 mono-font text-[10px] uppercase tracking-wider px-4 py-2 rounded-full bg-[#17B352] hover:bg-[#22D060] text-black font-bold cursor-pointer">
            <Plus className="w-3.5 h-3.5" />
            Add clip ({slotsLeft} left)
            <input
              ref={fileRef}
              data-testid="site-audio-upload-input"
              type="file"
              accept="audio/*,.mp3,.m4a,.ogg,.wav,.aac"
              onChange={onUpload}
              disabled={busy}
              className="hidden"
            />
          </label>
        ) : (
          <span className="mono-font text-[10px] text-[#52525B] uppercase tracking-wider">Max {maxTracks} clips reached — remove one to upload another</span>
        )}
        {hasTracks && (
          <>
            <button data-testid="site-audio-toggle-enabled"
              onClick={() => setEnabled(!enabled)}
              disabled={busy}
              className="mono-font text-xs uppercase tracking-wider px-4 py-2 rounded-full border border-[#1f1f23] text-[#A1A1AA] hover:border-[#17B352] hover:text-[#17B352]">
              {enabled ? "Disable" : "Enable"}
            </button>
            <button data-testid="site-audio-remove-all"
              onClick={removeAll}
              disabled={busy}
              className="mono-font text-xs uppercase tracking-wider px-4 py-2 rounded-full border border-[#1f1f23] text-[#FF453A] hover:bg-[rgba(255,69,58,0.05)] inline-flex items-center gap-1.5">
              <Trash2 className="w-3.5 h-3.5" /> Remove all
            </button>
          </>
        )}
      </div>

      {/* Volume slider */}
      {hasTracks && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="mono-font text-[10px] text-[#52525B] uppercase tracking-wider">Volume</span>
            <span className="mono-font text-[10px] text-[#17B352]" data-testid="site-audio-volume-label">{Math.round(volume * 100)}%</span>
          </div>
          <input
            data-testid="site-audio-volume-slider"
            type="range" min="0" max="1" step="0.01"
            value={volume}
            onChange={(e) => setVolume(parseFloat(e.target.value))}
            className="w-full accent-[#17B352]"
          />
          <p className="mono-font text-[9px] text-[#52525B] uppercase tracking-wider mt-1">Recommended: 15–20% for ambient layering</p>
        </div>
      )}
    </div>
  );
};

// ── Integrations Panel ──
const IntegrationsPanel = ({ token }) => {
  const [form, setForm] = useState({ slack_webhook_url: "", telegram_bot_token: "", telegram_chat_id: "" });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/integrations`,
        { headers: { Authorization: `Bearer ${token}` } });
      setForm(data);
    } finally {
      setLoaded(true);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/integrations`, form,
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Integrations saved");
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const test = async (channel) => {
    setTesting(true);
    try {
      const { data } = await axios.post(`${API}/admin/integrations/test`, { channel },
        { headers: { Authorization: `Bearer ${token}` } });
      if (data?.status === "no channels configured") {
        toast.error("No channels configured yet");
      } else {
        const ok = Object.values(data?.results || {}).every((s) => s === "ok");
        ok ? toast.success("Test ping delivered") : toast.error(`Result: ${JSON.stringify(data?.results)}`);
      }
    } catch {
      toast.error("Test failed");
    } finally {
      setTesting(false);
    }
  };

  if (!loaded) return <p className="mono-font text-xs text-[#52525B]">Loading...</p>;

  return (
    <div className="space-y-4" data-testid="integrations-panel">
      {/* Site Audio */}
      <SiteAudioPanel token={token} />

      {/* Slack */}
      <div className="glass-card rounded-xl p-5 space-y-3">
        <div className="flex items-center gap-3">
          <MessageSquare className="w-5 h-5 text-[#17B352]" />
          <h3 className="text-lg">Slack mirror</h3>
        </div>
        <p className="text-[#A1A1AA] text-sm">
          Get every contact, translator, and job submission instantly in a Slack channel. Create an
          <a href="https://api.slack.com/messaging/webhooks" target="_blank" rel="noreferrer" className="text-[#17B352] hover:underline ml-1">incoming webhook</a> and paste the URL below.
        </p>
        <input
          data-testid="slack-webhook-input"
          type="url"
          value={form.slack_webhook_url || ""}
          onChange={(e) => setForm({ ...form, slack_webhook_url: e.target.value })}
          placeholder="https://hooks.slack.com/services/..."
          className="w-full px-4 py-3 bg-[#0B0B0D] border border-[#1f1f23] rounded-lg mono-font text-xs text-white placeholder-[#52525B] focus:border-[#17B352]/60 focus:outline-none"
        />
        <button data-testid="slack-test-btn"
          onClick={() => test("slack")}
          disabled={!form.slack_webhook_url || testing}
          className="mono-font text-xs uppercase tracking-wider px-4 py-2 rounded-full border border-[#1f1f23] text-[#A1A1AA] hover:border-[#17B352] hover:text-[#17B352] disabled:opacity-50">
          Send test ping
        </button>
      </div>

      {/* Telegram */}
      <div className="glass-card rounded-xl p-5 space-y-3">
        <div className="flex items-center gap-3">
          <Send className="w-5 h-5 text-[#17B352]" />
          <h3 className="text-lg">Telegram mirror</h3>
        </div>
        <p className="text-[#A1A1AA] text-sm">
          Use the @BotFather to create a bot and get the token. Then DM the bot once and visit
          <span className="mono-font text-xs text-[#17B352] mx-1">/getUpdates</span> to find your chat_id.
        </p>
        <input
          data-testid="telegram-token-input"
          type="text"
          value={form.telegram_bot_token || ""}
          onChange={(e) => setForm({ ...form, telegram_bot_token: e.target.value })}
          placeholder="Bot token (e.g. 1234567890:ABC...)"
          className="w-full px-4 py-3 bg-[#0B0B0D] border border-[#1f1f23] rounded-lg mono-font text-xs text-white placeholder-[#52525B] focus:border-[#17B352]/60 focus:outline-none"
        />
        <input
          data-testid="telegram-chat-input"
          type="text"
          value={form.telegram_chat_id || ""}
          onChange={(e) => setForm({ ...form, telegram_chat_id: e.target.value })}
          placeholder="Chat ID (e.g. 123456789 or -100123…)"
          className="w-full px-4 py-3 bg-[#0B0B0D] border border-[#1f1f23] rounded-lg mono-font text-xs text-white placeholder-[#52525B] focus:border-[#17B352]/60 focus:outline-none"
        />
        <button data-testid="telegram-test-btn"
          onClick={() => test("telegram")}
          disabled={!(form.telegram_bot_token && form.telegram_chat_id) || testing}
          className="mono-font text-xs uppercase tracking-wider px-4 py-2 rounded-full border border-[#1f1f23] text-[#A1A1AA] hover:border-[#17B352] hover:text-[#17B352] disabled:opacity-50">
          Send test ping
        </button>
      </div>

      <div className="flex justify-end">
        <button data-testid="integrations-save-btn" onClick={save} disabled={saving}
          className="mono-font text-xs uppercase tracking-wider px-6 py-3 rounded-full bg-[#17B352] hover:bg-[#22D060] text-black font-bold inline-flex items-center gap-2 disabled:opacity-50">
          <Check className="w-4 h-4" /> {saving ? "Saving..." : "Save"}
        </button>
      </div>
    </div>
  );
};

// ── Resend Action Button (per submission row) ──
const ResendButton = ({ id, kind, token, onDone }) => {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const fire = async (target) => {
    setBusy(true);
    setOpen(false);
    try {
      await axios.post(`${API}/admin/resend-email`,
        { id, kind, target },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${target === "auto_reply" ? "Auto-reply" : "Team notification"} re-sent`);
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Resend failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="relative inline-block">
      <button
        data-testid={`resend-btn-${kind}-${id?.slice(0, 6)}`}
        disabled={busy}
        onClick={() => setOpen(!open)}
        className="mono-font text-[9px] tracking-wider uppercase px-2 py-1 rounded-full border border-[#1f1f23] text-[#A1A1AA] hover:border-[#17B352] hover:text-[#17B352] transition-colors disabled:opacity-50 inline-flex items-center gap-1"
      >
        <RefreshCw className={`w-3 h-3 ${busy ? "animate-spin" : ""}`} />
        Resend
      </button>
      {open && (
        <div className="absolute right-0 mt-1 z-30 bg-[#111114] border border-[#1f1f23] rounded-lg shadow-xl py-1 min-w-[170px]">
          <button data-testid={`resend-auto-${kind}-${id?.slice(0,6)}`}
            onClick={() => fire("auto_reply")}
            className="w-full text-left px-3 py-2 text-xs text-[#D4D4D8] hover:bg-[rgba(23,179,82,0.05)] hover:text-[#17B352]">
            Resend auto-reply
          </button>
          <button data-testid={`resend-notif-${kind}-${id?.slice(0,6)}`}
            onClick={() => fire("notification")}
            className="w-full text-left px-3 py-2 text-xs text-[#D4D4D8] hover:bg-[rgba(23,179,82,0.05)] hover:text-[#17B352]">
            Re-fire team notification
          </button>
        </div>
      )}
    </div>
  );
};

// ── Collapsible Table ──
const DataTable = ({ title, icon: Icon, items, columns, emptyText, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="glass-card rounded-xl overflow-hidden">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between p-5 text-left">
        <div className="flex items-center gap-3">
          <Icon className="w-5 h-5 text-[#17B352]" />
          <h3 className="text-lg">{title}</h3>
          <span className="mono-font text-xs text-[#52525B] ml-1">({items.length})</span>
        </div>
        {open ? <ChevronUp className="w-5 h-5 text-[#52525B]" /> : <ChevronDown className="w-5 h-5 text-[#52525B]" />}
      </button>
      {open && (
        <div className="border-t border-[rgba(255,255,255,0.05)]">
          {items.length === 0 ? (
            <p className="p-5 text-[#52525B] mono-font text-sm">{emptyText}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[rgba(255,255,255,0.05)]">
                    {columns.map((c) => (
                      <th key={c.key} className="px-5 py-3 text-left mono-font text-[10px] text-[#52525B] uppercase tracking-wider">{c.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, i) => (
                    <tr key={i} className="border-b border-[rgba(255,255,255,0.03)] hover:bg-[rgba(255,255,255,0.02)]">
                      {columns.map((c) => (
                        <td key={c.key} className="px-5 py-3 text-[#A1A1AA]">
                          {c.render ? c.render(item) : (item[c.key] || "—")}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── Admin Dashboard ──
const AdminPage = () => {
  const [token, setToken] = useState(localStorage.getItem("admin_token"));
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showJobForm, setShowJobForm] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");

  const headers = { Authorization: `Bearer ${token}` };

  const fetchDashboard = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/dashboard`, { headers: { Authorization: `Bearer ${token}` } });
      setDashboard(data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("admin_token");
        setToken(null);
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) fetchDashboard();
  }, [token, fetchDashboard]);

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    setToken(null);
  };

  const handleDeleteJob = async (jobId) => {
    if (!window.confirm("Delete this job posting?")) return;
    try {
      await axios.delete(`${API}/admin/jobs/${jobId}`, { headers });
      toast.success("Job deleted");
      fetchDashboard();
    } catch {
      toast.error("Failed to delete");
    }
  };

  if (!token) return <LoginScreen onLogin={setToken} />;
  if (loading) return <div className="min-h-screen bg-[#0B0B0D] flex items-center justify-center mono-font text-[#52525B]">Loading...</div>;

  const s = dashboard?.stats || {};
  const tabs = [
    { id: "overview", label: "Overview", icon: BarChart3 },
    { id: "portfolio", label: "Portfolio", icon: Film },
    { id: "talent", label: "Talent", icon: UserPlus },
    { id: "contacts", label: "Contacts", icon: Mail },
    { id: "translators", label: "Translators", icon: Globe },
    { id: "jobs", label: "Job Postings", icon: Briefcase },
    { id: "applications", label: "Applications", icon: FileText },
    { id: "emails", label: "Emails", icon: Inbox },
    { id: "integrations", label: "Integrations", icon: Settings },
  ];

  const formatDate = (d) => {
    if (!d) return "—";
    const date = typeof d === "string" ? new Date(d) : d;
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  return (
    <div className="min-h-screen bg-[#0B0B0D] text-[#E5E5E5]">
      {/* Top Bar */}
      <nav className="fixed top-0 left-0 right-0 z-50 nav-scrolled">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="inline-flex items-center"><img src="/graphikpro-logo.png" alt="Graphikpro" className="h-8 w-auto" /></Link>
            <span className="mono-font text-[10px] text-[#52525B] uppercase tracking-wider border border-[#1f1f23] px-2 py-0.5 rounded">Admin</span>
          </div>
          <button data-testid="admin-logout" onClick={handleLogout}
            className="flex items-center gap-2 mono-font text-xs text-[#52525B] hover:text-[#17B352] transition-colors uppercase tracking-wider">
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 pt-20 pb-12">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
          <StatCard icon={Eye} label="Total Views" value={s.total_views || 0} color="#007AFF" />
          <StatCard icon={Eye} label="This Week" value={s.week_views || 0} color="#32D74B" />
          <StatCard icon={Mail} label="Contacts" value={s.total_contacts || 0} color="#FF9F0A" />
          <StatCard icon={Users} label="Translators" value={s.total_translator_apps || 0} color="#BF5AF2" />
          <StatCard icon={Briefcase} label="Active Jobs" value={s.active_jobs || 0} color="#17B352" />
          <StatCard icon={FileText} label="Job Apps" value={s.total_job_apps || 0} color="#FF375F" />
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-1 mb-6 border-b border-[#1f1f23] pb-1">
          {tabs.map((t) => (
            <button key={t.id} onClick={() => setActiveTab(t.id)} data-testid={`tab-${t.id}`}
              className={`flex items-center gap-2 mono-font text-xs uppercase tracking-wider px-4 py-2.5 rounded-t transition-all ${
                activeTab === t.id ? "text-[#17B352] bg-[rgba(23,179,82,0.08)] border-b-2 border-[#17B352]" : "text-[#52525B] hover:text-[#A1A1AA]"
              }`}>
              <t.icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === "overview" && (
          <div className="space-y-4">
            <DataTable title="Recent Contact Submissions" icon={Mail}
              items={(dashboard?.recent_contacts || []).slice(0, 10)}
              columns={[
                { key: "name", label: "Name" },
                { key: "email", label: "Email" },
                { key: "company", label: "Company" },
                { key: "created_at", label: "Date", render: (r) => formatDate(r.created_at) },
              ]}
              emptyText="No contact submissions yet" />
            <DataTable title="Recent Translator Applications" icon={Globe}
              items={(dashboard?.recent_translator_apps || []).slice(0, 10)}
              columns={[
                { key: "name", label: "Name" },
                { key: "email", label: "Email" },
                { key: "language_pair", label: "Languages" },
                { key: "created_at", label: "Date", render: (r) => formatDate(r.created_at) },
              ]}
              emptyText="No translator applications yet" />
          </div>
        )}

        {activeTab === "portfolio" && (
          <PortfolioAdminPanel token={token} />
        )}

        {activeTab === "talent" && (
          <TalentAdminPanel token={token} />
        )}

        {activeTab === "contacts" && (
          <DataTable title="All Contact Submissions" icon={Mail}
            items={dashboard?.recent_contacts || []}
            columns={[
              { key: "name", label: "Name" },
              { key: "email", label: "Email" },
              { key: "company", label: "Company" },
              { key: "message", label: "Message", render: (r) => <span className="line-clamp-2 max-w-xs block">{r.message}</span> },
              { key: "email_status", label: "Email", render: (r) => (
                <div className="space-y-1">
                  <EmailStatusCell row={r} />
                  <ResendButton id={r.id} kind="contact" token={token} onDone={fetchDashboard} />
                </div>
              )},
              { key: "created_at", label: "Date", render: (r) => formatDate(r.created_at) },
            ]}
            emptyText="No contact submissions" />
        )}

        {activeTab === "translators" && (
          <DataTable title="All Translator Applications" icon={Globe}
            items={dashboard?.recent_translator_apps || []}
            columns={[
              { key: "name", label: "Name" },
              { key: "email", label: "Email" },
              { key: "language_pair", label: "Languages" },
              { key: "experience", label: "Experience", render: (r) => <span className="line-clamp-2 max-w-xs block">{r.experience}</span> },
              { key: "cv_url", label: "CV", render: (r) => r.cv_url ? <a href={r.cv_url} target="_blank" rel="noreferrer" className="text-[#17B352] hover:underline">View</a> : "—" },
              { key: "email_status", label: "Email", render: (r) => (
                <div className="space-y-1">
                  <EmailStatusCell row={r} />
                  <ResendButton id={r.id} kind="translator" token={token} onDone={fetchDashboard} />
                </div>
              )},
              { key: "created_at", label: "Date", render: (r) => formatDate(r.created_at) },
            ]}
            emptyText="No translator applications" />
        )}

        {activeTab === "jobs" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-xl">Job Postings</h3>
              <button data-testid="create-job-btn" onClick={() => { setEditingJob(null); setShowJobForm(true); }}
                className="glow-button flex items-center gap-2 text-sm py-2 px-5">
                <Plus className="w-4 h-4" /> New Job
              </button>
            </div>
            {(dashboard?.all_jobs || []).length === 0 ? (
              <div className="glass-card rounded-xl p-8 text-center">
                <p className="text-[#52525B] mono-font text-sm">No job postings yet. Create your first one.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {(dashboard?.all_jobs || []).map((job) => (
                  <div key={job.id} className="glass-card rounded-xl p-5 flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-3">
                        <h4 className="text-lg">{job.title}</h4>
                        <span className={`mono-font text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${
                          job.is_active ? "text-[#32D74B] border-[#32D74B]/30" : "text-[#52525B] border-[#52525B]/30"
                        }`}>
                          {job.is_active ? "Active" : "Inactive"}
                        </span>
                      </div>
                      <div className="flex gap-4 mono-font text-xs text-[#52525B] mt-1">
                        <span>{job.department}</span>
                        <span>{job.location}</span>
                        <span>{job.type}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button data-testid={`edit-job-${job.id}`} onClick={() => { setEditingJob(job); setShowJobForm(true); }}
                        className="p-2 rounded-lg hover:bg-[rgba(255,255,255,0.05)] text-[#A1A1AA] hover:text-[#17B352] transition-colors">
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button data-testid={`delete-job-${job.id}`} onClick={() => handleDeleteJob(job.id)}
                        className="p-2 rounded-lg hover:bg-[rgba(255,255,255,0.05)] text-[#A1A1AA] hover:text-[#FF375F] transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === "applications" && (
          <DataTable title="All Job Applications" icon={FileText}
            items={dashboard?.recent_job_apps || []}
            columns={[
              { key: "job_title", label: "Position" },
              { key: "name", label: "Name" },
              { key: "email", label: "Email" },
              { key: "resume_url", label: "Resume", render: (r) => r.resume_url ? <a href={r.resume_url} target="_blank" rel="noreferrer" className="text-[#17B352] hover:underline">{r.resume_filename || "View"}</a> : "—" },
              { key: "cover_letter", label: "Cover Letter", render: (r) => <span className="line-clamp-2 max-w-xs block">{r.cover_letter || "—"}</span> },
              { key: "email_status", label: "Email", render: (r) => (
                <div className="space-y-1">
                  <EmailStatusCell row={r} />
                  <ResendButton id={r.id} kind="job" token={token} onDone={fetchDashboard} />
                </div>
              )},
              { key: "created_at", label: "Date", render: (r) => formatDate(r.created_at) },
            ]}
            emptyText="No job applications yet" />
        )}

        {activeTab === "emails" && <EmailLogPanel token={token} />}

        {activeTab === "integrations" && <IntegrationsPanel token={token} />}
      </div>

      <AnimatePresence>
        {showJobForm && (
          <JobFormModal job={editingJob} token={token}
            onClose={() => setShowJobForm(false)}
            onSave={() => { setShowJobForm(false); fetchDashboard(); }} />
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminPage;
